import { Injectable } from '@angular/core';
import { aciesDateFormat, getUpdatedByFromMeta, IUserNameMap } from '@shared/helpers';
import { IBase, IDescription, IMeta, IName, ISelf } from '@shared/models/api/base';
import { combineLatest, map, Observable } from 'rxjs';
import { IConfigurationsResponse } from '../../masterdata/palletising/configuration/types';
import { CrudService } from './crud.service';

export type TGroupingType = 'Type' | 'Dimension';
export type TGroupingTypes = ['Type', 'Dimension'];
export type TEndPoints = '/palletising/palletdimensions' | '/palletising/pallettypes';

export interface ICreateZipCode {
	ruleNumber: number;
	to: string;
	from: string;
	description?: string;
}

export interface IZipCode extends ICreateZipCode, ISelf, IMeta {}

export interface ICreateGrouping extends IDescription {
	name: string | null;
	type: TGroupingType;
}

export interface IGrouping extends IName, IDescription, ISelf {
	type: TGroupingType;
	updatedDate: string;
	updatedAtEpoch: number;
	updatedBy: string | null;
}

export interface IPalletConfiguration {
	length: number;
	width: number;
	height: number;
	weight: number | null;
	maxHeight: number | null;
	maxWeight: number | null;
	oversize: number | null;
	spaceBetween: number | null;
	maxLayers: number | null;
	active: boolean | null;
}

export interface IPallet extends IName, IDescription, ISelf, IMeta {
	configuration: IPalletConfiguration;
	dimension: ISelf | null;
	type: ISelf | null;
	zipCodeRule: number | null;
}

export interface IUIPallet extends IDescription, ISelf {
	name: string;
	updatedAt: string;
	updatedAtEpoch?: number; // kept beside updatedAt by the list, which sorts by it; a new pallet has none yet
	updatedBy: string;
	length: number;
	width: number;
	height: number;
	weight: number | null;
	maxHeight: number | null;
	maxWeight: number | null;
	oversize: number | null;
	spaceBetween: number | null;
	maxLayers: number | null;
	active: boolean | null;
	activeLabel: string;
	dimension: IBase | null;
	dimensionName: string;
	type: IBase | null;
	typeName: string;
	zipCodeRule: number | null;
}

export interface ListResponse<T> {
	items: T[];
}

@Injectable({
	providedIn: 'root',
})
export class PalletisingService {
	constructor(private crudService: CrudService) {}

	public loadGroupings(users: IUserNameMap): Observable<IGrouping[]> {
		return combineLatest([this.fetchPalletDimensions(), this.fetchPalletTypes()]).pipe(
			map((data) => {
				const dimensions = data[0].items;
				const types = data[1].items;
				return [
					...dimensions.map((item) => {
						return this.baseToGrouping(item, 'Dimension', users);
					}),
					...types.map((item) => {
						return this.baseToGrouping(item, 'Type', users);
					}),
				];
			}),
		);
	}

	public fetchPalletTypes(): Observable<ListResponse<IBase>> {
		return this.crudService.get<ListResponse<IBase>>('/palletising/pallettypes');
	}

	public fetchPalletDimensions(): Observable<ListResponse<IBase>> {
		return this.crudService.get<ListResponse<IBase>>('/palletising/palletdimensions');
	}

	private baseToGrouping({ description, name, self, meta }: IBase, type: TGroupingType, users: IUserNameMap): IGrouping {
		return {
			description: description || null,
			name,
			self,
			type,
			updatedDate: aciesDateFormat(new Date(meta.updatedAt), true, false),
			updatedAtEpoch: meta.updatedAt,
			updatedBy: getUpdatedByFromMeta(users, meta),
		};
	}

	public createGrouping(grouping: ICreateGrouping): Observable<IBase> {
		if (grouping.type === 'Type') {
			return this.crudService.create<ICreateGrouping, IBase>(grouping, '/palletising/pallettypes');
		}
		return this.crudService.create<ICreateGrouping, IBase>(grouping, '/palletising/palletdimensions');
	}

	public updateGrouping(grouping: IGrouping): Observable<IBase> {
		return this.crudService.update(grouping);
	}

	public deleteGrouping(grouping: IGrouping): Observable<object> {
		return this.crudService.delete(grouping);
	}

	public loadZipCodes(users?: IUserNameMap): Observable<IZipCode[]> {
		return this.fetchZipCodes().pipe(
			map((data) => {
				return data.items.map((item) => {
					return {
						...item,
						updatedDate: aciesDateFormat(new Date(item.meta.updatedAt), true, false),
						updatedAtEpoch: item.meta.updatedAt,
						updatedBy: users ? getUpdatedByFromMeta(users, item.meta) : 'unknown',
					};
				});
			}),
		);
	}

	public fetchZipCode(zipCodeId: string): Observable<IZipCode> {
		return this.crudService.get<IZipCode>(`/palletising/zipcodes/${zipCodeId}`);
	}

	public fetchZipCodes(): Observable<{ items: IZipCode[] }> {
		return this.crudService.get<{ items: IZipCode[] }>('/palletising/zipcodes');
	}

	public createZipCode(zipCode: ICreateZipCode): Observable<IZipCode> {
		return this.crudService.create<ICreateZipCode, IZipCode>(zipCode, '/palletising/zipcodes');
	}

	public updateZipCode(zipCode: IZipCode): Observable<IZipCode> {
		return this.crudService.update<IZipCode>(zipCode);
	}

	public deleteZipCode(zipCode: IZipCode): Observable<object> {
		return this.crudService.delete(zipCode);
	}

	public deleteZipCodeById(zipCodeId: string): Observable<object> {
		return this.crudService.delete(`/palletising/zipcodes/${zipCodeId}`);
	}

	public deleteZipCodeBySelf(zipCodeself: string): Observable<object> {
		if (!zipCodeself.startsWith('/palletising/zipcodes')) {
			throw new Error('Invalid self URL');
		}

		return this.crudService.delete(zipCodeself);
	}

	public fetchPallets(): Observable<{ items: IPallet[] }> {
		return this.crudService.get<{ items: IPallet[] }>('/palletising/pallets');
	}

	private isPallet(pallet: IUIPallet | IPallet): pallet is IPallet {
		return (pallet as IPallet).configuration !== undefined;
	}

	public createPallet(pallet: IUIPallet | IPallet): Observable<void> {
		return this.crudService.create<Omit<IPallet, 'meta'>, void>(this.toPalletPayload(pallet), '/palletising/pallets');
	}

	public updatePallet(pallet: IUIPallet | IPallet): Observable<void> {
		return this.crudService.update(this.toPalletPayload(pallet));
	}

	public deletePallet(pallet: IUIPallet): Observable<object> {
		return this.crudService.delete(pallet);
	}

	public fetchConfigurations(): Observable<IConfigurationsResponse> {
		return this.crudService.get<IConfigurationsResponse>('/palletising/configurations');
	}

	// meta is read-only server side, so it is stripped before the pallet is sent
	private toPalletPayload(pallet: IUIPallet | IPallet): Omit<IPallet, 'meta'> {
		if (!this.isPallet(pallet)) {
			return this.iUIPalletToPallet(pallet);
		}

		const { meta: _meta, ...payload } = pallet;

		return payload;
	}

	private iUIPalletToPallet({ active, description, dimension, height, length, maxHeight, maxLayers, maxWeight, name, oversize, self, spaceBetween, type, weight, width, zipCodeRule }: IUIPallet): Omit<IPallet, 'meta'> {
		return {
			configuration: {
				active,
				height,
				length,
				maxHeight,
				maxLayers,
				maxWeight,
				oversize,
				spaceBetween,
				weight,
				width,
			},
			description,
			dimension,
			name,
			self,
			type,
			zipCodeRule,
		};
	}
}
